import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useOffline } from '../../context/OfflineContext';
import { SignOut, Warning } from '@phosphor-icons/react';
import ConfirmModal from '../UI/ConfirmModal';
import MytineriLogo from './MytineriLogo';
import SyncBanner from './SyncBanner';
import RateLimitBanner from './RateLimitBanner';
import { navItems } from './NavIcons';
import { SkipLink } from '../../utils/accessibility';

const LOGOUT_URL = 'https://travel-itinerary.cloudflareaccess.com/cdn-cgi/access/logout';

/**
 * Perform logout: clear local data and redirect
 */
function performLogout() {
  // Clear IndexedDB
  if ('indexedDB' in window) {
    indexedDB.databases().then(dbs => {
      dbs.forEach(db => {
        if (db.name?.startsWith('travel-itinerary')) {
          indexedDB.deleteDatabase(db.name);
        }
      });
    }).catch(() => {});
  }
  
  // Clear caches
  if ('caches' in window) {
    caches.keys().then(names => {
      names.forEach(name => caches.delete(name));
    }).catch(() => {});
  }
  
  // Clear storage except theme
  try {
    const theme = localStorage.getItem('theme');
    localStorage.clear();
    if (theme) localStorage.setItem('theme', theme);
    sessionStorage.clear();
  } catch (e) {}
  
  window.location.replace(LOGOUT_URL);
}

/**
 * Layout - Main application shell with navigation
 */
export default function Layout({ children }) {
  const { user } = useAuth();
  const { 
    isOnline, 
    isSyncing, 
    pendingSyncCount, 
    triggerSync,
    isAuthExpired,
    dismissAuthExpired,
    handleReLogin,
  } = useOffline();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  function handleLogoutClick(e) {
    e.preventDefault();
    
    if (!isOnline || pendingSyncCount > 0) {
      setShowLogoutConfirm(true);
    } else {
      performLogout();
    }
  }

  return (
    <div className="min-h-screen bg-canvas">
      {/* Skip link for keyboard navigation */}
      <SkipLink href="#main-content" />
      
      {/* Auth Expiry Modal */}
      <ConfirmModal
        isOpen={isAuthExpired}
        onClose={dismissAuthExpired}
        onConfirm={handleReLogin}
        title="Session Expired"
        message="Your session has expired. Would you like to log in again to sync your changes? If you cancel, you can still view existing events but changes won't be saved until you log in."
        confirmText="Log In Again"
        confirmVariant="primary"
        cancelText="Continue Viewing"
        icon={<Warning size={24} className="text-amber-500" weight="fill" />}
      />

      {/* Offline Logout Confirmation Modal */}
      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={() => {
          setShowLogoutConfirm(false);
          performLogout();
        }}
        title="Logout While Offline?"
        message={
          pendingSyncCount > 0
            ? `You have ${pendingSyncCount} unsaved change${pendingSyncCount !== 1 ? 's' : ''} that will be lost if you logout now. These changes have not been synced to the server and cannot be recovered.`
            : "You are currently offline. If you logout now, you won't be able to log back in until you have an internet connection."
        }
        confirmText="Logout Anyway"
        confirmVariant="danger"
        icon={<Warning size={24} className="text-amber-500" weight="fill" />}
      />

      {/* Sync/Offline Banner */}
      <SyncBanner
        isOnline={isOnline}
        pendingSyncCount={pendingSyncCount}
        isSyncing={isSyncing}
        onTriggerSync={triggerSync}
      />

      {/* Rate Limit Banner */}
      <RateLimitBanner />

      {/* Header */}
      <header 
        className="shadow-sm"
        style={{ 
          backgroundColor: 'var(--color-header-bg)',
          borderBottom: '1px solid var(--color-border-subtle)',
        }}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div 
                className="w-9 h-9 rounded-lg flex items-center justify-center shadow-sm overflow-hidden"
                style={{ backgroundColor: 'var(--color-bg-canvas)' }}
                aria-hidden="true"
              >
                <MytineriLogo className="w-7 h-7" />
              </div>
              <div>
                <h1 
                  className="font-display font-semibold text-lg leading-tight"
                  style={{ color: 'var(--color-header-text)' }}
                >
                  mytineri
                </h1>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => 
                    `flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all duration-200 ${
                      isActive ? 'nav-item-active' : 'nav-item'
                    }`
                  }
                  aria-current={({ isActive }) => isActive ? 'page' : undefined}
                >
                  <item.icon className="w-4 h-4" aria-hidden="true" />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* User info */}
            <div className="hidden md:flex items-center gap-3">
              <span className="text-sm" style={{ color: 'var(--color-header-text-muted)' }}>
                {user?.email}
              </span>
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center accent-element"
                aria-hidden="true"
              >
                <span className="text-sm font-medium">{user?.email?.[0]?.toUpperCase()}</span>
              </div>
              <a
                href={LOGOUT_URL}
                onClick={handleLogoutClick}
                className="p-2 rounded-sm transition-colors nav-item"
                aria-label="Logout"
              >
                <SignOut size={20} weight="thin" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav 
        className="md:hidden fixed bottom-0 left-0 right-0 shadow-lg z-50"
        style={{ 
          backgroundColor: 'var(--color-bg-canvas)',
          borderTop: '1px solid var(--color-border-subtle)',
        }}
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className="flex flex-col items-center gap-1 px-3 py-2 rounded-sm transition-all duration-200"
              style={({ isActive }) => ({
                color: isActive ? 'var(--color-brand-primary)' : 'var(--color-text-muted)',
              })}
            >
              <item.icon className="w-5 h-5" aria-hidden="true" />
              <span className="text-xs font-medium">{item.label}</span>
            </NavLink>
          ))}
          <a
            href={LOGOUT_URL}
            onClick={handleLogoutClick}
            className="flex flex-col items-center gap-1 px-3 py-2 rounded-sm transition-all duration-200"
            style={{ color: 'var(--color-text-muted)' }}
            aria-label="Logout"
          >
            <SignOut size={20} weight="thin" aria-hidden="true" />
            <span className="text-xs font-medium">Logout</span>
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <main 
        id="main-content"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-6"
        role="main"
      >
        {children}
      </main>
    </div>
  );
}
